import { ComponentProps, ComponentWithDropdown } from '@ardoq/dropdown-menu';
import { AnimatedChevron } from '@ardoq/icons';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { aggregatesWithLabel } from './consts';
import { Aggregate, Aggregates } from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';

interface AggregateSelectProps {
  aggregates: Maybe<Aggregates>;
  onAggregateSelect: (selectedAggregate: Aggregate) => void;
  selectedAggregate: Aggregate | null;
  aggregateLabels?: Partial<Record<Aggregate, string>>;
}

export const resolveAggregateLabel = (
  aggregate: Aggregate,
  aggregateLabels: AggregateSelectProps['aggregateLabels']
): string => aggregateLabels?.[aggregate] ?? aggregatesWithLabel[aggregate];

const AggregateLabel = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;
const AggregateSelectButton = forwardRef<
  HTMLDivElement,
  Pick<AggregateSelectProps, 'selectedAggregate' | 'aggregateLabels'> &
    ComponentProps
>(({ isMenuOpened, onMenuOpen, selectedAggregate, aggregateLabels }, ref) => {
  const aggregateLabel = selectedAggregate
    ? resolveAggregateLabel(selectedAggregate, aggregateLabels)
    : 'Aggregates';

  return (
    <AggregateLabel
      ref={ref}
      onClick={onMenuOpen}
      data-click-id="report-builder-aggregate-select-button"
    >
      {aggregateLabel}
      <AnimatedChevron $isUp={isMenuOpened} />
    </AggregateLabel>
  );
});

const getAggregateSelectOption = (
  aggregates: Maybe<Aggregates>,
  onAggregateSelect: AggregateSelectProps['onAggregateSelect'],
  aggregateLabels: AggregateSelectProps['aggregateLabels']
) =>
  aggregates
    ? Object.keys(aggregatesWithLabel)
        .filter(value =>
          Object.values(aggregates)?.some(aggregateForField =>
            Object.keys(aggregateForField).includes(value)
          )
        )
        .map(value => ({
          label: resolveAggregateLabel(value as Aggregate, aggregateLabels),
          value,
          onClick: () => onAggregateSelect(value as Aggregate),
        }))
    : [];

export const AggregateSelect = ({
  aggregates,
  selectedAggregate,
  onAggregateSelect,
  aggregateLabels,
}: AggregateSelectProps) => (
  <ComponentWithDropdown
    options={getAggregateSelectOption(
      aggregates,
      onAggregateSelect,
      aggregateLabels
    )}
    Component={AggregateSelectButton}
    alignMenuTo="left"
    componentProps={{ selectedAggregate, aggregateLabels }}
  />
);
